<template>
  <Dialog :visible="manageKnowledgeSourceDialogVisible"
          @update:visible="handleClose">
    <template #header>
      {{ manageKnowledgeSourceId ? 'Edit' : 'Add' }} Knowledge Source
    </template>

    <ScrollPanel class="scroll-panel" style="max-height: 60vh">
      <div class="form-element">
        Name:
        <InputText v-model="manageKnowledgeSourceName" :invalid="manageKnowledgeSourceNameInvalid" class="form-element full-width" type="text"
                  @change="manageKnowledgeSourceNameChange"/>
        <div v-if="manageKnowledgeSourceNameInvalid">
          <InlineMessage severity="error">Name is required.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        Type:
        <Dropdown :options="computedKnowledgeSourceTypes" option-label="name" option-value="id" option-disabled="disabled"
                  v-model="manageKnowledgeSourceType"
                  :invalid="manageKnowledgeSourceTypeInvalid"
                  filter
                  :auto-filter-focus="true"
                  class="ml-half"
                  @change="manageKnowledgeSourceTypeChange"
        ></Dropdown>
        <div class="mt-half" v-if="manageKnowledgeSourceTypeInvalid">
          <InlineMessage severity="error">Type is required.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        Description:
        <InputText v-model="manageKnowledgeSourceDescription" class="form-element full-width" type="text"/>
      </div>

      <div v-if="manageKnowledgeSourceId && manageKnowledgeSourceHasCredential && isSharepointType" class="form-element">
        <Button label="Configure SharePoint Sites" icon="pi pi-cog" @click="openSharepointSitesDialog" />
      </div>

      <div v-if="manageKnowledgeSourceId && manageKnowledgeSourceHasCredential && isGoogleDriveType" class="form-element">
        <Button label="Configure Google Drive" icon="pi pi-cog" @click="openGoogleDriveDialog" />
      </div>

      <template v-if="manageKnowledgeSourceHasCredential">
        Credentials have been entered for this Knowledge Source.
      </template>
      <Fieldset @update:collapsed="updateCredentialsCollapsed" :legend="(manageKnowledgeSourceHasCredential ? 'Input New ' : '') + 'Credentials'"
                :toggleable="manageKnowledgeSourceHasCredential" :collapsed="manageKnowledgeSourceHasCredential">
        <template v-if="isExactlySharepointType">
          <div class="form-element">
            Tenant ID:
            <InputText v-model="manageKnowledgeSourceCredentialTenantId" :invalid="manageKnowledgeSourceCredentialsInvalid && !manageKnowledgeSourceCredentialTenantId" class="form-element full-width" type="text"
                      @change="manageKnowledgeSourceCredentialsChange"/>
          </div>
          <div class="form-element">
            Client ID:
            <InputText v-model="manageKnowledgeSourceCredentialClientId" :invalid="manageKnowledgeSourceCredentialsInvalid && !manageKnowledgeSourceCredentialClientId" class="form-element full-width" type="text"
                      @change="manageKnowledgeSourceCredentialsChange"/>
          </div>
          <div class="form-element">
            Client Secret:
            <Password v-model="manageKnowledgeSourceCredentialClientSecret" :invalid="manageKnowledgeSourceCredentialsInvalid && !manageKnowledgeSourceCredentialClientSecret" class="form-element full-width" :feedback="false"
                      @change="manageKnowledgeSourceCredentialsChange"/>
          </div>
          <div v-if="manageKnowledgeSourceCredentialsInvalid">
            <InlineMessage severity="error">All credential fields are required.</InlineMessage>
          </div>
        </template>
        <template v-else>
          <div class="form-element">
            Credential Value:
            <Textarea v-model="manageKnowledgeSourceCredentialRawValue" :invalid="manageKnowledgeSourceCredentialsInvalid && !manageKnowledgeSourceCredentialRawValue" class="form-element full-width" rows="15" autoResize
                      @change="manageKnowledgeSourceCredentialsChange"/>
          </div>
          <div v-if="manageKnowledgeSourceCredentialsInvalid">
            <InlineMessage severity="error">Credential value is required.</InlineMessage>
          </div>
        </template>
      </Fieldset>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" type="button" @click="handleClose"></Button>
        <Button :disabled="manageKnowledgeSourceSubmitting" :loading="manageKnowledgeSourceSubmitting" label="Save" type="button" @click="saveKnowledgeSource"></Button>
      </div>
    </template>
  </Dialog>

  <Dialog :visible="sharepointSitesDialogVisible"
          @update:visible="closeSharepointSitesDialog"
          modal
          header="Configure SharePoint Sites"
          :style="{ width: '80vw' }">

    <ScrollPanel class="scroll-panel" style="max-height: 60vh">

      <div v-if="sitesLoading" class="text-center p-5">
        <ProgressSpinner style="width: 60px; height: 60px; margin-bottom: 1rem" />
        <h3>Loading SharePoint Sites</h3>
        <p class="text-secondary">
          Retrieving site information and available drives from your SharePoint configuration.
          This may take a moment...
        </p>
      </div>

      <div v-else-if="siteMetadata.length === 0" class="text-center p-5">
        <!-- When connection status is pending -->
        <template v-if="currentConnectionStatus && currentConnectionStatus.status === 'pending'">
          <i class="pi pi-hourglass" style="font-size: 3rem; color: #F59E0B; margin-bottom: 1rem"></i>
          <h3>Connection In Progress</h3>
          <p>The system is still establishing a connection to SharePoint. Please check back shortly.</p>
          <p class="text-sm text-secondary mb-3" v-if="currentConnectionStatus.lastConnectionAttempt">
            Last connection attempt: {{ formatDate(currentConnectionStatus.lastConnectionAttempt, true) }}
          </p>
          <Button icon="pi pi-refresh" label="Check Status" class="mt-2" @click="refreshSiteMetadata" />
        </template>

        <!-- When connection status is failed -->
        <template v-else-if="currentConnectionStatus && currentConnectionStatus.status === 'failed'">
          <i class="pi pi-exclamation-circle" style="font-size: 3rem; color: #EF4444; margin-bottom: 1rem"></i>
          <h3>Connection Failed</h3>
          <p>Unable to connect to SharePoint. Please verify your credentials and configuration.</p>
          <div v-if="currentConnectionStatus.errorMessage" class="p-3 bg-red-50 border-round mb-3 text-left">
            <strong>Error:</strong> {{ currentConnectionStatus.errorMessage }}
          </div>
          <Button icon="pi pi-refresh" label="Try Again" class="mt-2" @click="refreshSiteMetadata" />
        </template>

        <!-- When connection status is connected but no sites -->
        <template v-else-if="currentConnectionStatus && currentConnectionStatus.status === 'connected'">
          <i class="pi pi-check-circle" style="font-size: 3rem; color: #10B981; margin-bottom: 1rem"></i>
          <h3>Connected Successfully</h3>
          <p>Connection to SharePoint is working, but no sites were found.</p>
          <p class="mb-3">You may need to configure site permissions in your SharePoint admin console.</p>
          <Button icon="pi pi-refresh" label="Refresh Sites" class="mt-2" @click="refreshSiteMetadata" />
        </template>

        <!-- When connection status is unknown or not set -->
        <template v-else>
          <i class="pi pi-clock" style="font-size: 3rem; color: var(--primary-color); margin-bottom: 1rem"></i>
          <h3>Waiting for Connection</h3>
          <p>The system is establishing a connection to SharePoint.</p>
          <p class="mb-3">It may take a few minutes for the initial connection to be established and sites to be discovered.</p>
          <Button icon="pi pi-refresh" label="Check Status" class="mt-2" @click="refreshSiteMetadata" />
        </template>
      </div>

      <div v-else>
        <div class="mb flex center-items">
          <h3 class="flex-1">Available Sites</h3>
          <Button icon="pi pi-refresh" label="Refresh" @click="refreshSiteMetadata" :loading="sitesRefreshing" />
        </div>

        <div class="mb">
          <div class="p-inputgroup">
            <span class="p-inputgroup-addon">
              <i class="pi pi-search"></i>
            </span>
            <InputText v-model="siteSearchTerm" placeholder="Search sites..." class="full-width" />
          </div>
        </div>

        <!-- Add pagination controls -->
        <div class="mb full-width">
          <div class="text-secondary full-width align-center">
            <template v-if="filteredSites.length">
              Showing {{ firstVisibleSiteIndex + 1 }} to {{ Math.min(firstVisibleSiteIndex + sitesPerPage, filteredSites.length) }} of {{ filteredSites.length }} sites
            </template>
            <template v-else>
              No sites match the current filter.
            </template>
          </div>
          <Paginator 
            :first="firstVisibleSiteIndex"
            :rows="sitesPerPage"
            :total-records="filteredSites.length"
            :rows-per-page-options="[25, 50]"
            @page="handlePageChange"
            @update:rows="handleRowsChange"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          />
        </div>

        <Accordion :activeIndex="activeAccordionIndex" :multiple="true" @update:activeIndex="handleAccordionChange">
          <AccordionTab v-for="(site, siteIndex) in paginatedSites" :key="site.id">
            <template #header>
              <div class="flex center-items">
                <span class="mr-2">{{ site.name }}</span>
                <span class="mr-2"><small>{{ site.webUrl }}</small></span>
                
                <Badge v-if="site.enabled" 
                       value="Full Site" 
                       severity="info" 
                       class="ml" />
                <Badge v-else-if="getDriveSelectionStatus(site) === 'all'" 
                       value="All Drives" 
                       severity="success" 
                       class="ml" />
                <Badge v-else-if="getDriveSelectionStatus(site) === 'some'" 
                       value="Some Drives" 
                       severity="warning" 
                       class="ml" />
                <Badge v-else-if="getDriveSelectionStatus(site) === 'none'" 
                       value="No Drives" 
                       severity="secondary" 
                       class="ml" />
              </div>
            </template>
            
            <div class="mb-2">
              <div class="flex center-items">
                <Checkbox :id="`site-${siteIndex}`"
                          v-model="site.enabled"
                          binary
                          class="mr-2" />
                <label :for="`site-${siteIndex}`" class="font-bold">Enable Entire Site</label>
                <small class="ml-3 text-secondary">When enabled, all content will be synchronized without filtering by drive or path</small>
              </div>
            </div>
            
            <div class="mb-2 drives-container" :class="{ 'opacity-50': site.enabled }">
              <h4>Available Drives</h4>
              <div v-if="!site.availableDrives || site.availableDrives.length === 0" class="text-center">
                <InlineMessage severity="info">No drives found for this site.</InlineMessage>
              </div>
              <div v-else>
                <p class="text-secondary mb-2">Select individual drives to synchronize specific content. For each selected drive, you can further refine what content is synchronized using include and exclude paths.</p>
                <div v-for="drive in site.availableDrives" :key="drive.id" class="mb-3 pb-3 border-bottom-1 border-300">
                  <div class="flex center-items mb-2">
                    <Checkbox :id="`drive-${siteIndex}-${drive.id}`"
                            :value="true"
                            :modelValue="isDriveSelected(site, drive.id)"
                            @update:modelValue="toggleDrive(site, drive)"
                            :disabled="site.enabled"
                            :binary="true" />
                    <label :for="`drive-${siteIndex}-${drive.id}`" class="ml-2 font-bold">{{ drive.name }}</label>
                  </div>
                      
                  <!-- Only show paths if drive is selected -->
                  <div v-if="isDriveSelected(site, drive.id)" class="ml-4 pl-2 border-left-2 border-primary">
                    <div class="mb-2">
                      <h5>Include Paths</h5>
                      <small class="text-secondary block mb-2">Specify paths to include from this drive. Only content matching these paths will be synchronized. Leave empty to include everything.</small>
                      <div class="p-inputgroup mb-half">
                        <InputText v-model="drivePathInputs[drive.id].include" 
                                 placeholder="Add include path (e.g., /documents)" 
                                 :disabled="site.enabled" />
                        <Button icon="pi pi-plus" 
                              @click="addDrivePath(site, drive.id, 'include')" 
                              :disabled="site.enabled" />
                      </div>
                      <div v-for="(path, pathIndex) in getDrivePaths(site, drive.id, 'include')" 
                           :key="`inc-${drive.id}-${pathIndex}`" 
                           class="mt-1 flex center-items path-row">
                        <div class="flex-1">{{ path }}</div>
                        <Button icon="pi pi-times" 
                              severity="danger" 
                              text 
                              @click="removeDrivePath(site, drive.id, 'include', pathIndex)" 
                              :disabled="site.enabled"
                              v-tooltip.top="'Remove this path'" />
                      </div>
                    </div>
                      
                    <div class="mb-2">
                      <h5>Exclude Paths</h5>
                      <small class="text-secondary block mb-2">Specify paths to exclude from this drive. Content matching these paths will not be synchronized, even if they match an include path.</small>
                      <div class="p-inputgroup mb-half">
                        <InputText v-model="drivePathInputs[drive.id].exclude" 
                                 placeholder="Add exclude path (e.g., /temp)" 
                                 :disabled="site.enabled" />
                        <Button icon="pi pi-plus" 
                              @click="addDrivePath(site, drive.id, 'exclude')" 
                              :disabled="site.enabled" />
                      </div>
                      <div v-for="(path, pathIndex) in getDrivePaths(site, drive.id, 'exclude')" 
                           :key="`exc-${drive.id}-${pathIndex}`" 
                           class="mt-1 flex center-items path-row">
                        <div class="flex-1">{{ path }}</div>
                        <Button icon="pi pi-times" 
                              severity="danger" 
                              text 
                              @click="removeDrivePath(site, drive.id, 'exclude', pathIndex)" 
                              :disabled="site.enabled"
                              v-tooltip.top="'Remove this path'" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AccordionTab>
        </Accordion>
      </div>
    </ScrollPanel>
    
    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" @click="closeSharepointSitesDialog"></Button>
        <Button label="Save" icon="pi pi-save" :loading="sitesConfigSaving" @click="saveSiteConfigurations"></Button>
      </div>
    </template>
  </Dialog>

  <Dialog :visible="googleDriveDialogVisible"
          @update:visible="closeGoogleDriveDialog"
          modal
          header="Configure Google Drive"
          :style="{ width: '80vw' }">

    <ScrollPanel class="scroll-panel" style="max-height: 60vh">

      <div v-if="googleDriveLoading" class="text-center p-5">
        <ProgressSpinner style="width: 60px; height: 60px; margin-bottom: 1rem" />
        <h3>Loading Google Drive</h3>
        <p class="text-secondary">
          Retrieving drive information from your Google Drive configuration.
          This may take a moment...
        </p>
      </div>

      <div v-else-if="googleDriveMetadata.length === 0" class="text-center p-5">
        <template v-if="googleDriveConnectionStatus && googleDriveConnectionStatus.status === 'pending'">
          <i class="pi pi-hourglass" style="font-size: 3rem; color: #F59E0B; margin-bottom: 1rem"></i>
          <h3>Connection In Progress</h3>
          <p>The system is still establishing a connection to Google Drive. Please check back shortly.</p>
          <p class="text-sm text-secondary mb-3" v-if="googleDriveConnectionStatus.lastConnectionAttempt">
            Last connection attempt: {{ formatDate(googleDriveConnectionStatus.lastConnectionAttempt, true) }}
          </p>
          <Button icon="pi pi-refresh" label="Check Status" class="mt-2" @click="refreshGoogleDriveMetadata" />
        </template>

        <template v-else-if="googleDriveConnectionStatus && googleDriveConnectionStatus.status === 'failed'">
          <i class="pi pi-exclamation-circle" style="font-size: 3rem; color: #EF4444; margin-bottom: 1rem"></i>
          <h3>Connection Failed</h3>
          <p>Unable to connect to Google Drive. Please verify your credentials and configuration.</p>
          <div v-if="googleDriveConnectionStatus.errorMessage" class="p-3 bg-red-50 border-round mb-3 text-left">
            <strong>Error:</strong> {{ googleDriveConnectionStatus.errorMessage }}
          </div>
          <Button icon="pi pi-refresh" label="Try Again" class="mt-2" @click="refreshGoogleDriveMetadata" />
        </template>

        <template v-else-if="googleDriveConnectionStatus && googleDriveConnectionStatus.status === 'connected'">
          <i class="pi pi-check-circle" style="font-size: 3rem; color: #10B981; margin-bottom: 1rem"></i>
          <h3>Connected Successfully</h3>
          <p>Connection to Google Drive is working, but no drives were found.</p>
          <p class="mb-3">You may need to verify your Google Drive permissions.</p>
          <Button icon="pi pi-refresh" label="Refresh Drives" class="mt-2" @click="refreshGoogleDriveMetadata" />
        </template>

        <template v-else>
          <i class="pi pi-clock" style="font-size: 3rem; color: var(--primary-color); margin-bottom: 1rem"></i>
          <h3>Waiting for Connection</h3>
          <p>The system is establishing a connection to Google Drive.</p>
          <p class="mb-3">It may take a few minutes for the initial connection to be established and drives to be discovered.</p>
          <Button icon="pi pi-refresh" label="Check Status" class="mt-2" @click="refreshGoogleDriveMetadata" />
        </template>
      </div>

      <div v-else>
        <div class="mb flex center-items">
          <h3 class="flex-1">Available Drives</h3>
          <Button icon="pi pi-refresh" label="Refresh" @click="refreshGoogleDriveMetadata" :loading="googleDriveRefreshing" />
        </div>

        <div class="mb">
          <div class="p-inputgroup">
            <span class="p-inputgroup-addon">
              <i class="pi pi-search"></i>
            </span>
            <InputText v-model="googleDriveSearchTerm" placeholder="Search drives..." class="full-width" />
          </div>
        </div>

        <div class="mb full-width">
          <div class="text-secondary full-width align-center">
            <template v-if="filteredGoogleDrives.length">
              Showing {{ filteredGoogleDrives.length }} drive{{ filteredGoogleDrives.length !== 1 ? 's' : '' }}
            </template>
            <template v-else>
              No drives match the current filter.
            </template>
          </div>
        </div>

        <div v-for="drive in filteredGoogleDrives" :key="drive.id" class="mb-3 pb-3 border-bottom-1 border-300">
          <div class="flex center-items mb-2">
            <Checkbox :id="`gdrive-${drive.id}`"
                      v-model="drive.enabled"
                      binary
                      class="mr-2" />
            <label :for="`gdrive-${drive.id}`" class="font-bold">{{ drive.name }}</label>
          </div>

          <div v-if="drive.enabled" class="ml-4 pl-2 border-left-2 border-primary">
            <div class="mb-2">
              <h5>Include Paths</h5>
              <small class="text-secondary block mb-2">Specify paths to include from this drive. Only content matching these paths will be synchronized. Leave empty to include everything.</small>
              <div class="p-inputgroup mb-half">
                <InputText v-model="googleDrivePathInputs[drive.id].include"
                           placeholder="Add include path (e.g., /documents)" />
                <Button icon="pi pi-plus"
                        @click="addGoogleDrivePath(drive, 'include')" />
              </div>
              <div v-for="(path, pathIndex) in drive.config.includePaths"
                   :key="`ginc-${drive.id}-${pathIndex}`"
                   class="mt-1 flex center-items path-row">
                <div class="flex-1">{{ path }}</div>
                <Button icon="pi pi-times"
                        severity="danger"
                        text
                        @click="removeGoogleDrivePath(drive, 'include', pathIndex)"
                        v-tooltip.top="'Remove this path'" />
              </div>
            </div>

            <div class="mb-2">
              <h5>Exclude Paths</h5>
              <small class="text-secondary block mb-2">Specify paths to exclude from this drive. Content matching these paths will not be synchronized, even if they match an include path.</small>
              <div class="p-inputgroup mb-half">
                <InputText v-model="googleDrivePathInputs[drive.id].exclude"
                           placeholder="Add exclude path (e.g., /temp)" />
                <Button icon="pi pi-plus"
                        @click="addGoogleDrivePath(drive, 'exclude')" />
              </div>
              <div v-for="(path, pathIndex) in drive.config.excludePaths"
                   :key="`gexc-${drive.id}-${pathIndex}`"
                   class="mt-1 flex center-items path-row">
                <div class="flex-1">{{ path }}</div>
                <Button icon="pi pi-times"
                        severity="danger"
                        text
                        @click="removeGoogleDrivePath(drive, 'exclude', pathIndex)"
                        v-tooltip.top="'Remove this path'" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" @click="closeGoogleDriveDialog"></Button>
        <Button label="Save" icon="pi pi-save" :loading="googleDriveConfigSaving" @click="saveGoogleDriveConfigurations"></Button>
      </div>
    </template>
  </Dialog>

  <div v-if="hasAdminAccess">
    <ProgressSpinnerContainer v-if="knowledgeSourcesLoading" />
    <div v-else class="card">
      <div class="header-container mb flex">
        <h2 class="flex-1">Knowledge Sources</h2>
        <div class="align-right mr">
          <Button @click="manageKnowledgeSource(null)"><i class="pi pi-plus"></i> Add Knowledge Source</Button>
        </div>
      </div>
      <ScrollPanel class="scroll-panel" style="max-height: 60vh">
        <DataTable :value="knowledgeSources">
          <Column field="name" header="Name"></Column>
          <Column field="description" header="Description"></Column>
          <Column header="Credentials">
            <template #body="slotProps">
              <div class="relative">
                <template v-if="slotProps.data.hasCredentials">
                <i v-if="hasConnectionIssue(slotProps.data)"
                   class="pi pi-exclamation-triangle"
                   style="color: #F59E0B; font-size: 1.5em;"
                   v-tooltip.top="getConnectionErrorTooltip(slotProps.data)"></i>

                <i v-else-if="slotProps.data?.metadata?.connectionStatus?.status === 'connected'"
                   class="pi pi-check" 
                   style="color: #10B981; font-size: 1.5em;"
                   v-tooltip.top="getCredentialsTooltip(slotProps.data)"></i>

                <i v-else
                   class="pi pi-question"
                   style="color: #F59E0B; font-size: 1.5em;"
                   v-tooltip.top="getCredentialsTooltip(slotProps.data)"></i>
                </template>

                <i v-else
                   class="pi pi-times" 
                   style="color: #FF4444; font-size: 1.5em;"
                   v-tooltip.top="'No credentials provided'"></i>
                
              </div>
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button severity="info" @click="manageKnowledgeSource(slotProps.data)" label="Edit"></Button>
              <Button class="ml" severity="danger" @click="deleteKnowledgeSource(slotProps.data)" label="Delete"></Button>
            </template>
          </Column>
        </DataTable>
      </ScrollPanel>
    </div>
  </div>
  <div v-else>
    You do not have permission to access this page.
  </div>
</template>

<script setup>
import { useConfirm } from "primevue/useconfirm";
import ProgressSpinnerContainer from "../components/ProgressSpinnerContainer.vue";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { useAccounts, useKnowledgeSources, useUsers } from "../composables/entities.js";
import { selectedAccount } from "../store/state.js";
import { deleteRequest, patch, post, get } from "../util/api.js";
import { decodeKnowledgeSource, decodeLlmQuota } from "../util/util.js";

const { knowledgeSources, knowledgeSourceTypes, knowledgeSourcesLoading } = useKnowledgeSources()

const { hasAdminAccess } = useUsers()

const confirm = useConfirm()

const toast = useToast()

const computedKnowledgeSourceTypes = computed(() => {
  const output = []
  for (let i = 0; i < knowledgeSourceTypes.value?.length; i++) {
    const o = { ... knowledgeSourceTypes.value[i] }
    /* Temporary check to be removed later @todo */
    o.disabled = o.machineName !== 'SHAREPOINT' && o.machineName !== 'GOOGLE_DRIVE';
    output.push(o);
  }
  return output
})

async function deleteKnowledgeSource(data) {
  confirm.require({
    message: 'Are you sure you wish to delete ' + data.name + '?',
    header: 'Confirm Delete Knowledge Source',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        knowledgeSourcesLoading.value = true
        const request = await deleteRequest('/crbn-tech/ai-knowledge-source/' + data.id)
        if (request.id) {
          knowledgeSources.value = knowledgeSources.value.filter(ks => String(ks.id) !== String(request.id))
        }
        else {
          handleError()
        }
      }
      catch (e) {
        handleError(e)
      }
      finally {
        knowledgeSourcesLoading.value = false
      }
      return true
    },
    reject: () => {
      return true
    }
  })
}

watch(knowledgeSourcesLoading, (newValue)=> {
  if (!newValue) {
    if (!hasAdminAccess.value) {
      //router.replace('/')
    }
  }
}, { immediate: true })

const manageKnowledgeSourceDialogVisible = ref(false)
const manageKnowledgeSourceSubmitting = ref(false)

const manageKnowledgeSourceId = ref(null)
const manageKnowledgeSourceName = ref(null)
const manageKnowledgeSourceDescription = ref(null)
const manageKnowledgeSourceType = ref(null)
const manageKnowledgeSourceHasCredential = ref(false)

const manageKnowledgeSourceNameInvalid = ref(false)
const manageKnowledgeSourceTypeInvalid = ref(false)

const manageKnowledgeSourceCredentialTenantId = ref(null)
const manageKnowledgeSourceCredentialClientId = ref(null)
const manageKnowledgeSourceCredentialClientSecret = ref(null)
const manageKnowledgeSourceCredentialRawValue = ref(null)
const manageKnowledgeSourceCredentialsInvalid = ref(false)

const isExactlySharepointType = computed(() => {
  if (!manageKnowledgeSourceType.value) return false
  const selectedType = knowledgeSourceTypes.value?.find(t => t.id === manageKnowledgeSourceType.value)
  return selectedType?.machineName === 'SHAREPOINT'
})

function manageKnowledgeSourceNameChange() {
  manageKnowledgeSourceNameInvalid.value = !manageKnowledgeSourceName.value
}
function manageKnowledgeSourceCredentialsChange() {
  manageKnowledgeSourceCredentialsInvalid.value = false
}
function manageKnowledgeSourceTypeChange() {
  manageKnowledgeSourceTypeInvalid.value = false
}

function handleClose() {
  manageKnowledgeSourceId.value = null
  manageKnowledgeSourceName.value = null
  manageKnowledgeSourceDescription.value = null
  manageKnowledgeSourceHasCredential.value = false

  manageKnowledgeSourceCredentialTenantId.value = null
  manageKnowledgeSourceCredentialClientId.value = null
  manageKnowledgeSourceCredentialClientSecret.value = null
  manageKnowledgeSourceCredentialRawValue.value = null

  manageKnowledgeSourceNameInvalid.value = null
  manageKnowledgeSourceCredentialsInvalid.value = false

  manageKnowledgeSourceDialogVisible.value = false
  manageKnowledgeSourceSubmitting.value = false

  siteMetadata.value = [];
  activeAccordionIndex.value = null;
  openSiteIds.value = [];

  googleDriveMetadata.value = [];
  googleDrivePathInputs.value = {};
  googleDriveSearchTerm.value = '';

  credentialsCollapsed.value = false
}

function manageKnowledgeSource(data) {
  manageKnowledgeSourceDialogVisible.value = true
  manageKnowledgeSourceId.value = data?.id
  manageKnowledgeSourceName.value = data?.name
  manageKnowledgeSourceDescription.value = data?.description
  manageKnowledgeSourceType.value = data?.knowledgeSourceType
  manageKnowledgeSourceHasCredential.value = !!data?.hasCredentials

  if (!data?.id) {
    if (computedKnowledgeSourceTypes.value.length === 1) {
      manageKnowledgeSourceType.value = computedKnowledgeSourceTypes.value[0].id
    }
  }
}

function handleError(e) {
  manageKnowledgeSourceSubmitting.value = false
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'An error has occurred.  Please try again.'
  })
}

function resetValidations() {
  manageKnowledgeSourceNameInvalid.value = false
  manageKnowledgeSourceTypeInvalid.value = false
  manageKnowledgeSourceCredentialsInvalid.value = false
}

async function saveKnowledgeSource() {
  let valid = true
  resetValidations()
  if (!manageKnowledgeSourceName.value) {
    manageKnowledgeSourceNameInvalid.value = true
    valid = false
  }
  if (!manageKnowledgeSourceType.value) {
    manageKnowledgeSourceTypeInvalid.value = true
    valid = false
  }
  if (isExactlySharepointType.value) {
    /* Only invalid if one is entered but not all three */
    if (!!manageKnowledgeSourceCredentialTenantId.value || !!manageKnowledgeSourceCredentialClientId.value || !!manageKnowledgeSourceCredentialClientSecret.value) {
      if (!manageKnowledgeSourceCredentialTenantId.value || !manageKnowledgeSourceCredentialClientId.value || !manageKnowledgeSourceCredentialClientSecret.value) {
        manageKnowledgeSourceCredentialsInvalid.value = true
        valid = false
      }
    }
  }

  if (!valid) {
    return false
  }

  if (manageKnowledgeSourceId.value) {
    const payload = {
      title: manageKnowledgeSourceName.value,
      knowledge_source_types_id: manageKnowledgeSourceType.value,
      description: manageKnowledgeSourceDescription.value,
    }
    manageKnowledgeSourceSubmitting.value = true
    try {
      const result = await patch('/crbn-tech/ai-knowledge-source/' + manageKnowledgeSourceId.value, payload)
      if (result.id) {
        try {
          if (!credentialsCollapsed.value) {
            let credentialValue = null
            if (isExactlySharepointType.value) {
              if (manageKnowledgeSourceCredentialTenantId.value) {
                credentialValue = manageKnowledgeSourceCredentialTenantId.value +
                    ':' + manageKnowledgeSourceCredentialClientId.value +
                    ':' + manageKnowledgeSourceCredentialClientSecret.value
              }
            } else {
              credentialValue = manageKnowledgeSourceCredentialRawValue.value || null
            }
            if (credentialValue) {
              const credentialPayload = {
                credential_value: credentialValue,
                cephisso_platform_accounts_id: selectedAccount.value
              }
              const credentialResult = await post('/crbn-tech/ai-knowledge-source/' + result.id + '/credentials', credentialPayload)
              if (credentialResult.id) {
                result.has_credentials = true
              }
            }
          }
        }
        catch (e) {
          handleError(e)
        }

        for (let i = 0; i < knowledgeSources.value.length; i++) {
          if (String(knowledgeSources.value[i].id) === String(result.id)) {
            knowledgeSources.value[i] = decodeKnowledgeSource(result)
          }
        }
      }
      else {
        handleError();
      }
      handleClose()
    }
    catch (e) {
      manageKnowledgeSourceSubmitting.value = false
      handleError(e)
    }
  }
  else {
    const payload = {
      title: manageKnowledgeSourceName.value,
      knowledge_source_types_id: manageKnowledgeSourceType.value,
      description: manageKnowledgeSourceDescription.value,
      cephisso_platform_accounts_id: selectedAccount.value
    }
    manageKnowledgeSourceSubmitting.value = true
    try {
      const result = await post('/crbn-tech/ai-knowledge-source', payload)
      if (result.id) {
        try {
          let credentialValue = null
          if (isExactlySharepointType.value) {
            if (manageKnowledgeSourceCredentialTenantId.value) {
              credentialValue = manageKnowledgeSourceCredentialTenantId.value +
                  ':' + manageKnowledgeSourceCredentialClientId.value +
                  ':' + manageKnowledgeSourceCredentialClientSecret.value
            }
          } else {
            credentialValue = manageKnowledgeSourceCredentialRawValue.value || null
          }
          if (credentialValue) {
            const credentialPayload = {
              credential_value: credentialValue,
              cephisso_platform_accounts_id: selectedAccount.value
            }
            const credentialResult = await post('/crbn-tech/ai-knowledge-source/' + result.id + '/credentials', credentialPayload)
            if (credentialResult.id) {
              result.has_credentials = true
            }
          }
        }
        catch (e) {
          handleError(e)
        }

        knowledgeSources.value.push(decodeKnowledgeSource(result))
      }
      else {
        handleError()
      }
      handleClose()
    }
    catch (e) {
      manageKnowledgeSourceSubmitting.value = false
      handleError(e)
    }
  }
}

const credentialsCollapsed = ref(false)
function updateCredentialsCollapsed(v) {
    credentialsCollapsed.value = v
}

// Site metadata management
const siteMetadata = ref([]);
const sitesLoading = ref(false);
const activeAccordionIndex = ref(null);
const openSiteIds = ref([]); // Track open sites by their IDs
const siteSearchTerm = ref('');
const sitesPerPage = ref(25);
const firstVisibleSiteIndex = ref(0);

// Add watchers to keep firstVisibleSiteIndex in sync
watch(sitesPerPage, () => {
  firstVisibleSiteIndex.value = 0;
}, { immediate: true });

const isSharepointType = computed(() => {
  if (!manageKnowledgeSourceType.value) return false;
  const selectedType = knowledgeSourceTypes.value?.find(t => t.id === manageKnowledgeSourceType.value);
  return selectedType?.machineName === 'SHAREPOINT';
});

const isGoogleDriveType = computed(() => {
  if (!manageKnowledgeSourceType.value) return false;
  const selectedType = knowledgeSourceTypes.value?.find(t => t.id === manageKnowledgeSourceType.value);
  return selectedType?.machineName === 'GOOGLE_DRIVE';
});

// Add a new ref for the refresh loading state
const sitesRefreshing = ref(false);

// Add this new ref to hold the current connection status
const currentConnectionStatus = ref(null);

// Load site metadata from the server
async function loadSiteMetadata() {
  if (!manageKnowledgeSourceId.value) return;
  
  sitesLoading.value = true;
  try {
    // Get data directly from the knowledge source endpoint
    const result = await get(`/crbn-tech/ai-knowledge-source/${manageKnowledgeSourceId.value}`);
    
    if (result && result.metadata && result.metadata.sites) {
      // Map the sites and then sort them alphabetically by name
      siteMetadata.value = result.metadata.sites
        .map(site => ({
          ...site,
          drives: Array.isArray(site.drives) ? site.drives : [],
          availableDrives: site.availableDrives || [],
          enabled: site.enabled || false
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    } else {
      siteMetadata.value = [];
    }
  } catch (e) {
    toast.add({
      severity: 'error',
      group: 'mainToast',
      life: 5000,
      detail: 'Failed to load site metadata. Please try again.'
    });
    siteMetadata.value = [];
  } finally {
    sitesLoading.value = false;
  }
}

// Refresh site metadata from Sharepoint
async function refreshSiteMetadata() {
  if (!manageKnowledgeSourceId.value) return;
  
  sitesRefreshing.value = true;
  try {
    // Get the knowledge source to check connection status
    const knowledgeSource = await get(`/crbn-tech/ai-knowledge-source/${manageKnowledgeSourceId.value}`);
    
    // Store the connection status for use in the UI
    currentConnectionStatus.value = knowledgeSource?.metadata?.connectionStatus || null;
    
    // Update the stored entity in knowledgeSources
    updateStoredKnowledgeSource(knowledgeSource);
    
    // Then load the site data
    await loadSiteMetadata();
  } catch (e) {
    toast.add({
      severity: 'error',
      group: 'mainToast',
      life: 5000,
      detail: 'Failed to refresh ' + (currentConnectionStatus.value ? 'sites' : 'connection information') + '. Please try again.'
    });
  } finally {
    sitesRefreshing.value = false;
  }
}

const sharepointSitesDialogVisible = ref(false)

async function openSharepointSitesDialog() {
  // Show the dialog first
  sharepointSitesDialogVisible.value = true;
  sitesLoading.value = true;
  
  // Reset accordion state
  activeAccordionIndex.value = null;
  openSiteIds.value = [];
  
  try {
    // Get the knowledge source to check connection status
    const knowledgeSource = await get(`/crbn-tech/ai-knowledge-source/${manageKnowledgeSourceId.value}`);
    
    // Store the connection status for use in the UI
    currentConnectionStatus.value = knowledgeSource?.metadata?.connectionStatus || null;
    
    // Update the stored entity in knowledgeSources
    updateStoredKnowledgeSource(knowledgeSource);
    
    // Then load the site data
    await loadSiteMetadata();
    
    // Initialize drive path inputs
    drivePathInputs.value = {};
    siteMetadata.value.forEach(site => {
      site.drives.forEach(drive => {
        drivePathInputs.value[drive.id] = { include: '', exclude: '' };
      });
      
      // Also initialize inputs for all available drives
      if (site.availableDrives) {
        site.availableDrives.forEach(drive => {
          if (!drivePathInputs.value[drive.id]) {
            drivePathInputs.value[drive.id] = { include: '', exclude: '' };
          }
        });
      }
    });
  } catch (e) {
    // Error is already handled in loadSiteMetadata
    currentConnectionStatus.value = null;
  }
}

function closeSharepointSitesDialog() {
  sharepointSitesDialogVisible.value = false;
  currentConnectionStatus.value = null;
  drivePathInputs.value = {};
  siteSearchTerm.value = '';
  openSiteIds.value = [];
  activeAccordionIndex.value = null;
  firstVisibleSiteIndex.value = 0;
  sitesPerPage.value = 25;
}

const sitesConfigSaving = ref(false);

async function saveSiteConfigurations() {
  if (
    siteMetadata.value.length > 0 && 
    manageKnowledgeSourceId.value && 
    manageKnowledgeSourceHasCredential.value && 
    isSharepointType.value
  ) {
    sitesConfigSaving.value = true;
    try {
      // Get the latest metadata to avoid overwriting changes
      const currentKnowledgeSource = await get(`/crbn-tech/ai-knowledge-source/${manageKnowledgeSourceId.value}`);
      
      // Prepare updated metadata
      const updatedMetadata = { ...currentKnowledgeSource.metadata || {} };
      
      // Create a map of existing sites by name for easier merging
      const existingSitesMap = {};
      if (updatedMetadata.sites) {
        updatedMetadata.sites.forEach(site => {
          existingSitesMap[site.id] = site;
        });
      } else {
        updatedMetadata.sites = [];
      }
      
      // Update or add each site from our local state
      siteMetadata.value.forEach(site => {
        if (existingSitesMap[site.id]) {
          // Update existing site
          const existingSite = existingSitesMap[site.id];
          existingSite.drives = site.drives;
          existingSite.enabled = site.enabled;
        }
      });
      
      // Save the updated metadata
      const result = await patch(`/crbn-tech/ai-knowledge-source/${manageKnowledgeSourceId.value}`, {
        metadata: JSON.stringify(updatedMetadata)
      });
      
      // Update the stored entity with the result
      if (result && result.id) {
        updateStoredKnowledgeSource(result);
      }
      
      toast.add({
        severity: 'success',
        group: 'mainToast',
        life: 3000,
        detail: 'SharePoint site configurations saved successfully!'
      });
      
      // Close the dialog after successful save
      closeSharepointSitesDialog();
    } catch (e) {
      toast.add({
        severity: 'error',
        group: 'mainToast',
        life: 5000,
        detail: 'Failed to save site configurations. Please try again.'
      });
    } finally {
      sitesConfigSaving.value = false;
    }
  } else {
    // Just close the dialog if there's nothing to save
    closeSharepointSitesDialog();
  }
}

// Helper function to check if there are connection issues
function hasConnectionIssue(knowledgeSource) {
  const connectionStatus = knowledgeSource?.metadata?.connectionStatus;
  return connectionStatus && 
    (connectionStatus.status === 'failed' || connectionStatus.errorMessage);
}

// Helper function to get the tooltip text for connection errors
function getConnectionErrorTooltip(knowledgeSource) {
  const connectionStatus = knowledgeSource?.metadata?.connectionStatus;
  if (!connectionStatus) return '';
  
  if (connectionStatus.errorMessage) {
    return `Error: ${connectionStatus.errorMessage}`;
  } else if (connectionStatus.status === 'failed') {
    return 'Connection failed. Please check your credentials and configuration.';
  } else if (connectionStatus.status === 'pending') {
    return 'Connection pending. The system is still setting up this knowledge source.';
  }
  
  return '';
}

// Helper function to get the tooltip text for credentials
function getCredentialsTooltip(knowledgeSource) {
  const connectionStatus = knowledgeSource?.metadata?.connectionStatus;
  
  if (!connectionStatus) return 'Credentials have been entered.  The system has not yet validated the connection.';
  
  if (connectionStatus.status === 'connected' && connectionStatus.lastSyncCompleted) {
    return `Last sync: ${formatDate(connectionStatus.lastSyncCompleted)}`;
  } else if (connectionStatus.status === 'connected') {
    return 'Connected successfully, no sync completed yet';
  } else if (connectionStatus.status === 'pending' && !connectionStatus.errorMessage) {
    return 'Connection pending. The system is still setting up this knowledge source.';
  }
  
  return 'Credentials have been entered. The connection is being validated.';
}

// Simple date formatter
function formatDate(dateString, relative = false) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (relative) {
      const now = new Date();
      const diff = Math.floor((now - date) / 1000);
      if (diff < 60) {
        return `${diff} seconds ago`;
      } else if (diff < 3600) {
        return `${Math.floor(diff / 60)} minutes ago`;
      } else if (diff < 86400) {
        return `${Math.floor(diff / 3600)} hours ago`;
      } else {
        return `${Math.floor(diff / 86400)} days ago`;
      }
    } else {
      return date.toLocaleString(undefined, { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  } catch (e) {
    return dateString;
  }
}

// Add a helper function to update the stored knowledge source
function updateStoredKnowledgeSource(updatedKnowledgeSource) {
  if (!updatedKnowledgeSource || !updatedKnowledgeSource.id) return;
  
  // Find and update the knowledge source in the array
  for (let i = 0; i < knowledgeSources.value.length; i++) {
    if (String(knowledgeSources.value[i].id) === String(updatedKnowledgeSource.id)) {
      // Use the decodeKnowledgeSource function to maintain consistent formatting
      knowledgeSources.value[i] = decodeKnowledgeSource(updatedKnowledgeSource);
      break;
    }
  }
}

// Function to determine the drive selection status for a site
function getDriveSelectionStatus(site) {
  if (!site.availableDrives || site.availableDrives.length === 0) {
    return null;
  }
  if (!site.drives || !site.drives?.length) {
    return 'none';
  }
  if (site.drives.length === site.availableDrives.length) {
    return 'all';
  }
  return 'some';
}

// For managing path inputs for multiple drives
const drivePathInputs = ref({});

// Check if a drive is selected
function isDriveSelected(site, driveId) {
  // Ensure the drive path input is initialized
  if (!drivePathInputs.value[driveId]) {
    drivePathInputs.value[driveId] = { include: '', exclude: '' };
  }
  return site.drives.some(drive => drive.id === driveId);
}

// Get a drive config by ID
function getDriveConfig(site, driveId) {
  return site.drives.find(drive => drive.id === driveId);
}

// Get paths for a specific drive
function getDrivePaths(site, driveId, type) {
  const drive = getDriveConfig(site, driveId);
  if (!drive) return [];
  
  return type === 'include' ? drive.includePaths : drive.excludePaths;
}

// Toggle a drive selection
function toggleDrive(site, drive) {
  const existingDrive = getDriveConfig(site, drive.id);
  
  if (existingDrive) {
    // Remove if already selected
    site.drives = site.drives.filter(d => d.id !== drive.id);
  } else {
    // Add if not selected
    site.drives.push({
      id: drive.id,
      name: drive.name,
      includePaths: [],
      excludePaths: []
    });
    
    // Initialize input fields for this drive
    if (!drivePathInputs.value[drive.id]) {
      drivePathInputs.value[drive.id] = { include: '', exclude: '' };
    }
  }
}

// Add a path to a drive
function addDrivePath(site, driveId, type) {
  const drive = getDriveConfig(site, driveId);
  if (!drive) return;
  
  const inputKey = type === 'include' ? 'include' : 'exclude';
  const pathArray = type === 'include' ? drive.includePaths : drive.excludePaths;
  
  // Ensure drivePathInputs[driveId] exists
  if (!drivePathInputs.value[driveId]) {
    drivePathInputs.value[driveId] = { include: '', exclude: '' };
  }
  
  const inputValue = drivePathInputs.value[driveId][inputKey];
  
  if (inputValue && !pathArray.includes(inputValue)) {
    pathArray.push(inputValue);
    drivePathInputs.value[driveId][inputKey] = '';
  }
}

// Remove a path from a drive
function removeDrivePath(site, driveId, type, index) {
  const drive = getDriveConfig(site, driveId);
  if (!drive) return;
  
  const pathArray = type === 'include' ? drive.includePaths : drive.excludePaths;
  pathArray.splice(index, 1);
}

function matchesSiteFilter(site) {
  // If no search term, show all sites
  if (!siteSearchTerm.value || siteSearchTerm.value.trim() === '') {
    return true;
  }
  
  const searchTerm = siteSearchTerm.value.toLowerCase().trim();
  
  // Check if site name contains the search term
  if (site.name && site.name.toLowerCase().includes(searchTerm)) {
    return true;
  }

    // Check if site web URL contains the search term
  if (site.webUrl && site.webUrl.toLowerCase().includes(searchTerm)) {
    return true;
  }
  
  // Check if any drive names match the search term
  if (site.availableDrives && site.availableDrives.length > 0) {
    for (const drive of site.availableDrives) {
      if (drive.name && drive.name.toLowerCase().includes(searchTerm)) {
        return true;
      }
    }
  }
  
  // No matches found
  return false;
}

const filteredSites = computed(() => siteMetadata.value.filter(site => matchesSiteFilter(site)));
const paginatedSites = computed(() => filteredSites.value.slice(firstVisibleSiteIndex.value, firstVisibleSiteIndex.value + sitesPerPage.value));

// Function to handle page changes
function handlePageChange(e) {
  firstVisibleSiteIndex.value = e.first;
  // Reset accordion when changing pages
  activeAccordionIndex.value = null;
  openSiteIds.value = [];
}

// Watch for changes to the search term and update activeAccordionIndex based on filtered sites
watch(siteSearchTerm, () => {
  // Reset to first page when search term changes
  firstVisibleSiteIndex.value = 0;
  // Convert openSiteIds to indices in the filtered list
  updateAccordionIndices();
});

// Function to update accordion indices based on the open site IDs
function updateAccordionIndices() {
  if (!openSiteIds.value.length) {
    activeAccordionIndex.value = null;
    return;
  }
  
  // Map open site IDs to their indices in the filtered list
  const indices = filteredSites.value
    .map((site, index) => openSiteIds.value.includes(site.id) ? index : null)
    .filter(index => index !== null);
    
  activeAccordionIndex.value = indices.length ? indices : null;
}

// Update the openSiteIds when accordion changes
function handleAccordionChange(newValue) {
  if (!newValue) {
    openSiteIds.value = [];
    return;
  }
  
  // Convert indices to site IDs
  if (Array.isArray(newValue)) {
    openSiteIds.value = newValue.map(index => filteredSites.value[index]?.id).filter(Boolean);
  } else if (typeof newValue === 'number') {
    const siteId = filteredSites.value[newValue]?.id;
    if (siteId) {
      openSiteIds.value = [siteId];
    }
  }
}

// Watch filteredSites to update indices when the list changes
watch(filteredSites, () => {
  updateAccordionIndices();
}, { deep: true });

// Add the rows change handler function
function handleRowsChange(event) {
  // First update the rows per page
  sitesPerPage.value = event;
  // Reset to first page
  firstVisibleSiteIndex.value = 0;
  // Reset accordion state
  activeAccordionIndex.value = null;
  openSiteIds.value = [];
}

// ---- Google Drive configuration ----

const googleDriveDialogVisible = ref(false);
const googleDriveLoading = ref(false);
const googleDriveRefreshing = ref(false);
const googleDriveMetadata = ref([]);
const googleDriveConnectionStatus = ref(null);
const googleDriveSearchTerm = ref('');
const googleDrivePathInputs = ref({});
const googleDriveConfigSaving = ref(false);

const filteredGoogleDrives = computed(() =>
  googleDriveMetadata.value.filter(drive => {
    if (!googleDriveSearchTerm.value || googleDriveSearchTerm.value.trim() === '') return true;
    return drive.name && drive.name.toLowerCase().includes(googleDriveSearchTerm.value.toLowerCase().trim());
  })
);

async function loadGoogleDriveMetadata() {
  if (!manageKnowledgeSourceId.value) return;

  googleDriveLoading.value = true;
  try {
    const result = await get(`/crbn-tech/ai-knowledge-source/${manageKnowledgeSourceId.value}`);

    if (result && result.metadata && result.metadata.drives) {
      googleDriveMetadata.value = result.metadata.drives
        .map(drive => ({
          id: drive.id,
          name: drive.name,
          enabled: drive.enabled || false,
          config: {
            id: drive.config?.id || drive.id,
            name: drive.config?.name || drive.name,
            includePaths: Array.isArray(drive.config?.includePaths) ? [...drive.config.includePaths] : [],
            excludePaths: Array.isArray(drive.config?.excludePaths) ? [...drive.config.excludePaths] : [],
          },
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    } else {
      googleDriveMetadata.value = [];
    }
  } catch (e) {
    toast.add({
      severity: 'error',
      group: 'mainToast',
      life: 5000,
      detail: 'Failed to load Google Drive metadata. Please try again.',
    });
    googleDriveMetadata.value = [];
  } finally {
    googleDriveLoading.value = false;
  }
}

async function refreshGoogleDriveMetadata() {
  if (!manageKnowledgeSourceId.value) return;

  googleDriveRefreshing.value = true;
  try {
    const knowledgeSource = await get(`/crbn-tech/ai-knowledge-source/${manageKnowledgeSourceId.value}`);
    googleDriveConnectionStatus.value = knowledgeSource?.metadata?.connectionStatus || null;
    updateStoredKnowledgeSource(knowledgeSource);
    await loadGoogleDriveMetadata();
  } catch (e) {
    toast.add({
      severity: 'error',
      group: 'mainToast',
      life: 5000,
      detail: 'Failed to refresh Google Drive information. Please try again.',
    });
  } finally {
    googleDriveRefreshing.value = false;
  }
}

async function openGoogleDriveDialog() {
  googleDriveDialogVisible.value = true;
  googleDriveLoading.value = true;

  try {
    const knowledgeSource = await get(`/crbn-tech/ai-knowledge-source/${manageKnowledgeSourceId.value}`);
    googleDriveConnectionStatus.value = knowledgeSource?.metadata?.connectionStatus || null;
    updateStoredKnowledgeSource(knowledgeSource);
    await loadGoogleDriveMetadata();

    googleDrivePathInputs.value = {};
    googleDriveMetadata.value.forEach(drive => {
      googleDrivePathInputs.value[drive.id] = { include: '', exclude: '' };
    });
  } catch (e) {
    googleDriveConnectionStatus.value = null;
  }
}

function closeGoogleDriveDialog() {
  googleDriveDialogVisible.value = false;
  googleDriveConnectionStatus.value = null;
  googleDrivePathInputs.value = {};
  googleDriveSearchTerm.value = '';
  googleDriveMetadata.value = [];
}

function addGoogleDrivePath(drive, type) {
  if (!googleDrivePathInputs.value[drive.id]) {
    googleDrivePathInputs.value[drive.id] = { include: '', exclude: '' };
  }

  const inputValue = googleDrivePathInputs.value[drive.id][type];
  const pathArray = type === 'include' ? drive.config.includePaths : drive.config.excludePaths;

  if (inputValue && !pathArray.includes(inputValue)) {
    pathArray.push(inputValue);
    googleDrivePathInputs.value[drive.id][type] = '';
  }
}

function removeGoogleDrivePath(drive, type, index) {
  const pathArray = type === 'include' ? drive.config.includePaths : drive.config.excludePaths;
  pathArray.splice(index, 1);
}

async function saveGoogleDriveConfigurations() {
  if (
    googleDriveMetadata.value.length > 0 &&
    manageKnowledgeSourceId.value &&
    manageKnowledgeSourceHasCredential.value &&
    isGoogleDriveType.value
  ) {
    googleDriveConfigSaving.value = true;
    try {
      const currentKnowledgeSource = await get(`/crbn-tech/ai-knowledge-source/${manageKnowledgeSourceId.value}`);
      const updatedMetadata = { ...currentKnowledgeSource.metadata || {} };

      updatedMetadata.drives = googleDriveMetadata.value.map(drive => ({
        id: drive.id,
        name: drive.name,
        enabled: drive.enabled,
        config: {
          id: drive.config.id,
          name: drive.config.name,
          includePaths: [...drive.config.includePaths],
          excludePaths: [...drive.config.excludePaths],
        },
      }));

      const result = await patch(`/crbn-tech/ai-knowledge-source/${manageKnowledgeSourceId.value}`, {
        metadata: JSON.stringify(updatedMetadata),
      });

      if (result && result.id) {
        updateStoredKnowledgeSource(result);
      }

      toast.add({
        severity: 'success',
        group: 'mainToast',
        life: 3000,
        detail: 'Google Drive configurations saved successfully!',
      });

      closeGoogleDriveDialog();
    } catch (e) {
      toast.add({
        severity: 'error',
        group: 'mainToast',
        life: 5000,
        detail: 'Failed to save Google Drive configurations. Please try again.',
      });
    } finally {
      googleDriveConfigSaving.value = false;
    }
  } else {
    closeGoogleDriveDialog();
  }
}

</script>

<style scoped>
.path-row {
  border-radius: 6px;
  padding: 0.5em; 
}

.opacity-50 {
  opacity: 0.5;
}

.drives-container:not(.opacity-50) .path-row:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>